import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

export const fileUrl = (id) => `${API}/files/${id}`;

// CMS content sometimes stores absolute URLs baked to whatever host the owner edited on
// (e.g. the preview domain). On production that makes images load from the wrong backend and
// break. Rebind any absolute /api/files/ URL to the CURRENT backend origin so images always
// resolve against the site they're being viewed on.
const fixOrigin = (url) => {
  if (!url || typeof url !== "string") return url;
  const i = url.indexOf("/api/files/");
  if (i >= 0 && /^https?:\/\//i.test(url)) return `${BACKEND_URL}${url.slice(i)}`;
  return url;
};

export const sized = (url, w = 1000) => {
  if (!url) return url;
  url = fixOrigin(url);
  const sep = url.includes("?") ? "&" : "?";
  if (url.includes("cdn.shopify.com")) return `${url}${sep}width=${w}`;
  // Backend-hosted images support on-demand resizing via ?w= — critical so mobile
  // never downloads full-resolution originals (which stall/abort on slow connections).
  if (url.includes("/api/files/")) return `${url}${sep}w=${w}`;
  return url; // external CDNs (unsplash/pexels) already carry sizing params
};

// Tiny low-quality image placeholder (LQIP) for blur-up loading. Strips any existing width
// param first so we always request the tiny variant.
export const lqip = (url) => {
  if (!url) return undefined;
  url = fixOrigin(url);
  if (!(url.includes("/api/files/") || url.includes("cdn.shopify.com"))) return undefined;
  const base = url.replace(/[?&](w|width)=\d+/gi, "").replace(/\?&/, "?").replace(/&$/, "").replace(/\?$/, "");
  return sized(base, 32);
};

// Responsive srcset for backend-hosted (/api/files/) and Shopify images so the browser
// picks the smallest file that fits the slot / device pixel ratio. Returns undefined for
// external CDN URLs (they already carry their own sizing).
export const srcSet = (url, widths = [400, 800, 1200]) => {
  if (!url) return undefined;
  url = fixOrigin(url);
  const isShopify = url.includes("cdn.shopify.com");
  const isFiles = url.includes("/api/files/");
  if (!isShopify && !isFiles) return undefined;
  const param = isShopify ? "width" : "w";
  // Strip any existing width param so candidates aren't duplicated, then repair separators.
  let base = url.replace(/[?&](w|width)=\d+/gi, "");
  if (!base.includes("?") && base.includes("&")) base = base.replace("&", "?");
  const sep = base.includes("?") ? "&" : "?";
  return widths.map((w) => `${base}${sep}${param}=${w} ${w}w`).join(", ");
};

// Rebind a single image URL to the current backend origin IF it is a backend-hosted image:
// - relative "/api/files/…" paths are prefixed with the backend origin
// - absolute "…/api/files/…" URLs (e.g. baked to a preview domain) are re-homed via fixOrigin
// Legitimate external/CDN URLs (unsplash, pexels, shopify, etc.) are returned untouched.
const rebindBackendImage = (url) => {
  if (!url || typeof url !== "string") return url;
  if (url.startsWith("/api/files/")) return `${BACKEND_URL}${url}`;
  return fixOrigin(url); // only rewrites absolute URLs containing /api/files/
};

// Sanitize admin-authored story HTML before it is injected via dangerouslySetInnerHTML.
// Strips unsafe markup (scripts/styles/iframes/embeds), inline event handlers (on*), and
// javascript: URLs, while preserving the elements stories need (including <img>). Inline
// backend images are rebound to the current backend origin and given responsive srcset/sizes
// + lazy loading; external images are left as-is.
const UNSAFE_TAGS = new Set(["SCRIPT", "STYLE", "IFRAME", "OBJECT", "EMBED", "LINK", "META", "BASE", "FORM"]);
export const sanitizeStoryHtml = (html) => {
  if (!html || typeof html !== "string") return "";
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  const root = doc.body.firstChild;
  const walk = (node) => {
    [...node.childNodes].forEach((child) => {
      if (child.nodeType !== 1) return; // keep text/comments-as-text, only inspect elements
      if (UNSAFE_TAGS.has(child.tagName)) { node.removeChild(child); return; }
      [...child.attributes].forEach((a) => {
        const name = a.name.toLowerCase();
        if (name.startsWith("on")) { child.removeAttribute(a.name); return; }
        if ((name === "href" || name === "src" || name === "srcset" || name === "xlink:href")
            && /^\s*javascript:/i.test(a.value)) {
          child.removeAttribute(a.name);
        }
      });
      if (child.tagName === "IMG") {
        const raw = child.getAttribute("src");
        if (!raw || !raw.trim()) { node.removeChild(child); return; } // drop empty authoring artifacts
        const url = rebindBackendImage(raw);
        child.setAttribute("src", sized(url, 1000));
        const ss = srcSet(url, [400, 800, 1200]);
        if (ss) {
          child.setAttribute("srcset", ss);
          if (!child.hasAttribute("sizes")) child.setAttribute("sizes", "(min-width:640px) 720px, 100vw");
        } else {
          child.removeAttribute("srcset"); // drop any baked srcset that would bypass rebinding
        }
        if (!child.hasAttribute("loading")) child.setAttribute("loading", "lazy");
      }
      walk(child);
    });
  };
  walk(root);
  return root.innerHTML;
};

export const money = (amount, currency = "EUR") => {
  const sym = currency === "EUR" ? "€" : currency === "USD" ? "$" : currency + " ";
  return `${sym}${Number(amount).toFixed(2)}`;
};
